<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useLaboratoryStore, useProductStore } from '../../stores';
import { useNotificationStore } from '../../stores/notificationStore';
import { DELIVERY_TIMES } from '../../constants';
import type { ProductWithDetails, ComponentProduct } from '../../types';
import CompositeProductSelector from './CompositeProductSelector.vue';

const props = defineProps<{
  laboratories: ProductWithDetails['laboratories'];
  modelValue: ProductWithDetails['laboratories'];
  showGraduations?: boolean;
  productName?: string;
}>();

const emit = defineEmits(['update:modelValue', 'laboratory-change']);
const laboratoryStore = useLaboratoryStore();
const productStore = useProductStore();
const notificationStore = useNotificationStore();

// Estado para el modal de graduaciones y composición
const showCompositeSelector = ref(false);
const selectedLabIndex = ref<number | null>(null);

// Initialize modelValue with all laboratories from props if editing
onMounted(() => {
  if (props.laboratories && props.laboratories.length > 0) {
    const newLabs = [...props.laboratories].map(lab => ({
      laboratory: lab.laboratory,
      details: {
        ...lab.details,
        productId: lab.details.productId || '',
        laboratoryId: lab.laboratory.id!,
        PrecioDeCosto: lab.details.PrecioDeCosto || 0,
        TiempoDeDemora: lab.details.TiempoDeDemora || DELIVERY_TIMES[0],
        Disponible: lab.details.Disponible ?? true,
        UltimaActualizacion: lab.details.UltimaActualizacion || new Date(),
        graduations: lab.details.graduations || [],
        isCompuesto: lab.details.isCompuesto || false,
        componentes: lab.details.componentes || []
      }
    }));
    emit('update:modelValue', newLabs);
  }
});

const addLaboratory = () => {
  if (!laboratoryStore.laboratories.length) return;

  // Find a laboratory that's not already in the list
  const availableLab = laboratoryStore.laboratories.find(lab => 
    !props.modelValue.some(existing => existing.laboratory.id === lab.id)
  );

  if (!availableLab) {
    notificationStore.warning('No hay más laboratorios disponibles para agregar');
    return;
  }

  const newLabs = [...props.modelValue];
  newLabs.push({
    laboratory: availableLab,
    details: {
      productId: '',
      laboratoryId: availableLab.id!,
      PrecioDeCosto: 0,
      TiempoDeDemora: DELIVERY_TIMES[0],
      Disponible: true,
      UltimaActualizacion: new Date(),
      graduations: [],
      isCompuesto: false,
      componentes: []
    }
  });
  emit('update:modelValue', newLabs);
};

const removeLaboratory = (index: number) => {
  if (props.modelValue.length <= 1) return;
  const newLabs = [...props.modelValue];
  newLabs.splice(index, 1);
  emit('update:modelValue', newLabs);
};

const updateLaboratory = (index: number, field: string, event: Event) => {
  const newLabs = [...props.modelValue];
  const target = event.target as HTMLInputElement | HTMLSelectElement;
  
  switch (field) {
    case 'laboratory': {
      const selectedLab = laboratoryStore.laboratories.find(lab => lab.id === target.value);
      if (selectedLab) {
        newLabs[index].laboratory = selectedLab;
        newLabs[index].details.laboratoryId = selectedLab.id!;
        if (index === 0) {
          emit('laboratory-change', selectedLab.id);
        }
      }
      break;
    }
    case 'PrecioDeCosto':
      newLabs[index].details.PrecioDeCosto = Number(target.value) || 0;
      break;
    case 'TiempoDeDemora':
      newLabs[index].details.TiempoDeDemora = target.value;
      break;
    case 'Disponible':
      newLabs[index].details.Disponible = (target as HTMLInputElement).checked;
      break;
    case 'isCompuesto':
      newLabs[index].details.isCompuesto = (target as HTMLInputElement).checked;
      if (!(target as HTMLInputElement).checked) {
        newLabs[index].details.componentes = [];
      }
      break;
  }
  
  emit('update:modelValue', newLabs);
};

// Compute available laboratories for selection
const availableLaboratories = computed(() => {
  return laboratoryStore.laboratories.filter(lab => 
    props.modelValue.some(existing => existing.laboratory.id === lab.id) ||
    !props.modelValue.some(existing => existing.laboratory.id === lab.id)
  );
});

// Manejar selección de componentes
const handleComponentsSelect = (index: number) => {
  selectedLabIndex.value = index;
  showCompositeSelector.value = true;
};

const handleComponentsSave = (selectedIds: string[]) => {
  if (selectedLabIndex.value === null) return;

  const newLabs = [...props.modelValue];
  const lab = newLabs[selectedLabIndex.value];
  
  // Calcular precio total de los componentes
  const totalPrice = selectedIds.reduce((total, componentId) => {
    const component = productStore.products.find(p => p.id === componentId);
    if (!component) return total;

    const componentLab = component.laboratories.find(l => 
      l.laboratory.id === lab.laboratory.id
    );
    return total + (componentLab?.details.PrecioDeCosto || 0);
  }, 0);

  // Actualizar laboratorio
  lab.details.componentes = selectedIds;
  lab.details.PrecioDeCosto = totalPrice;
  
  emit('update:modelValue', newLabs);
  showCompositeSelector.value = false;
  selectedLabIndex.value = null;
};

// Obtener componentes seleccionados para un laboratorio
const getSelectedComponents = (index: number): ComponentProduct[] => {
  const lab = props.modelValue[index];
  if (!lab.details.componentes?.length) return [];

  return lab.details.componentes.map(id => {
    const product = productStore.products.find(p => p.id === id);
    const componentLab = product?.laboratories.find(l => 
      l.laboratory.id === lab.laboratory.id
    );
    return {
      id,
      nombre: product?.Nombre || '',
      precio: componentLab?.details.PrecioDeCosto || 0
    };
  });
};
</script>

<template>
  <div class="space-y-4">
    <div class="flex justify-between items-center">
      <h3 class="text-lg font-medium text-slate-800">Laboratorios</h3>
      <button 
        type="button"
        @click="addLaboratory"
        class="text-sm text-indigo-600 hover:text-indigo-800"
        :disabled="props.modelValue.length >= laboratoryStore.laboratories.length"
      >
        Agregar Laboratorio
      </button>
    </div>

    <div v-for="(lab, index) in modelValue" :key="index" class="space-y-4 p-4 bg-slate-50 rounded-lg">
      <div class="flex justify-between items-start">
        <h4 class="text-sm font-medium text-slate-700">Laboratorio {{ index + 1 }}</h4>
        <button 
          v-if="modelValue.length > 1"
          type="button"
          @click="removeLaboratory(index)"
          class="text-sm text-red-600 hover:text-red-800"
        >
          Eliminar
        </button>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium text-slate-700 mb-2">Laboratorio</label>
          <select 
            :value="lab.laboratory.id"
            @change="(e) => updateLaboratory(index, 'laboratory', e)"
            class="input-field"
            required
          >
            <option v-for="l in availableLaboratories" :key="l.id" :value="l.id">
              {{ l.Nombre }}
            </option>
          </select>
        </div>

        <div>
          <label class="block text-sm font-medium text-slate-700 mb-2">Tiempo de Demora</label>
          <select 
            :value="lab.details.TiempoDeDemora"
            @change="(e) => updateLaboratory(index, 'TiempoDeDemora', e)"
            class="input-field" 
            required
          >
            <option v-for="time in DELIVERY_TIMES" :key="time" :value="time">
              {{ time }}
            </option>
          </select>
        </div>

        <div class="md:col-span-2">
          <div class="flex items-center gap-2 mb-4">
            <input 
              type="checkbox"
              :checked="lab.details.isCompuesto"
              @change="(e) => updateLaboratory(index, 'isCompuesto', e)"
              class="rounded text-indigo-600 focus:ring-indigo-500"
            />
            <label class="text-sm text-slate-700">Precio compuesto</label>
          </div>

          <div v-if="lab.details.isCompuesto">
            <!-- Selector de componentes -->
            <div class="flex items-center justify-between mb-4">
              <div>
                <h5 class="text-sm font-medium text-slate-700">Componentes</h5>
                <p class="text-xs text-slate-500 mt-1">
                  Seleccione los productos que componen este precio
                </p>
              </div>
              <button
                type="button"
                @click="handleComponentsSelect(index)"
                class="btn-primary bg-white border border-indigo-500 text-indigo-500 hover:bg-indigo-50"
              >
                {{ lab.details.componentes?.length 
                  ? `${lab.details.componentes.length} componentes` 
                  : 'Seleccionar Componentes' 
                }}
              </button>
            </div>

            <!-- Lista de componentes -->
            <div v-if="getSelectedComponents(index).length > 0" class="mb-4">
              <div class="bg-white rounded-lg border border-slate-200 overflow-hidden">
                <table class="w-full">
                  <thead class="bg-slate-50">
                    <tr>
                      <th class="px-4 py-2 text-left text-sm font-medium text-slate-700">Nombre</th>
                      <th class="px-4 py-2 text-right text-sm font-medium text-slate-700">Precio</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-slate-200">
                    <tr v-for="component in getSelectedComponents(index)" :key="component.id">
                      <td class="px-4 py-2 text-sm">{{ component.nombre }}</td>
                      <td class="px-4 py-2 text-sm text-right">
                        ${{ component.precio.toLocaleString() }}
                      </td>
                    </tr>
                    <tr class="bg-slate-50">
                      <td class="px-4 py-2 text-sm font-medium">Total</td>
                      <td class="px-4 py-2 text-sm font-medium text-right">
                        ${{ getSelectedComponents(index)
                          .reduce((total, comp) => total + comp.precio, 0)
                          .toLocaleString() }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div v-else>
            <label class="block text-sm font-medium text-slate-700 mb-2">Precio de Costo</label>
            <input 
              :value="lab.details.PrecioDeCosto"
              @input="(e) => updateLaboratory(index, 'PrecioDeCosto', e)"
              type="number"
              min="0"
              step="0.01"
              class="input-field"
              required
            />
          </div>
        </div>

        <div class="flex items-center">
          <label class="flex items-center space-x-2">
            <input 
              type="checkbox"
              :checked="lab.details.Disponible"
              @change="(e) => updateLaboratory(index, 'Disponible', e)"
              class="rounded text-indigo-600 focus:ring-indigo-500"
            />
            <span class="text-sm text-slate-700">Disponible</span>
          </label>
        </div>
      </div>
    </div>

    <!-- Composite Product Selector Modal -->
    <CompositeProductSelector
      v-if="showCompositeSelector && selectedLabIndex !== null"
      :show="showCompositeSelector"
      :available-products="productStore.products"
      :selected-laboratory-id="modelValue[selectedLabIndex].laboratory.id!"
      :initial-selection="modelValue[selectedLabIndex].details.componentes"
      :product-name="productName"
      :laboratory-name="modelValue[selectedLabIndex].laboratory.Nombre"
      @close="showCompositeSelector = false"
      @save="handleComponentsSave"
    />
  </div>
</template>

<style scoped>
.input-field {
  @apply w-full p-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 
         focus:border-indigo-500 outline-none transition-all duration-200;
}
</style>